import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useProjects } from '../hooks/useProjects';
import { Project, ProjectFilters } from '../types/project';
import CommonPageHeader from '../components/common/CommonPageHeader';
import SearchBar from '../components/common/SearchBar';
import ProjectList from '../components/projects/ProjectList';
import ProjectForm from '../components/projects/ProjectForm';
import ProjectView from '../components/projects/ProjectView';
import LoadingScreen from '../components/common/LoadingScreen';
import { 
  PlusIcon, 
  ChartBarIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const Projects: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const {
    projects,
    loading,
    createProject,
    updateProject,
    deleteProject,
    updateProjectStatus,
    updateProjectPriority,
    getProjectStats
  } = useProjects();

  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [viewingProject, setViewingProject] = useState<Project | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<ProjectFilters>({});
  const [showFilters, setShowFilters] = useState(false);

  // Effect to handle FAB trigger for new project
  useEffect(() => {
    const handleFABNewProject = () => {
      setEditingProject(null);
      setShowForm(true);
    };

    window.addEventListener('openProjectForm', handleFABNewProject);
    return () => {
      window.removeEventListener('openProjectForm', handleFABNewProject);
    };
  }, []);

  // Jogosultság ellenőrzés
  if (!hasAccess('projects')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">{t('common.errors.loadingFailed')}</div>
      </div>
    );
  }

  // Projektek szűrése
  const filteredProjects = projects.filter(project => {
    // Szöveges keresés
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        project.name.toLowerCase().includes(searchLower) ||
        project.description.toLowerCase().includes(searchLower) ||
        project.customerName?.toLowerCase().includes(searchLower) ||
        project.tags.some(tag => tag.toLowerCase().includes(searchLower));
      
      if (!matchesSearch) return false;
    }

    // Státusz szűrés
    if (filters.status && project.status !== filters.status) return false;

    // Prioritás szűrés
    if (filters.priority && project.priority !== filters.priority) return false;

    // Hozzárendelt felhasználó szűrés
    if (filters.assignedUser && !project.assignedUsers.includes(filters.assignedUser)) return false;

    // Ügyfél szűrés
    if (filters.customerId && project.customerId !== filters.customerId) return false;

    return true;
  });

  // Projekt létrehozás
  const handleCreateProject = async (projectData: any) => {
    try {
      await createProject(projectData);
      setShowForm(false);
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  };

  // Projekt frissítés
  const handleUpdateProject = async (projectData: any) => {
    if (!editingProject) return;
    
    try {
      await updateProject(editingProject.id, projectData);
      setEditingProject(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  };

  // Projekt törlés
  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm(t('projects.confirmDelete') || 'Biztosan törölni szeretné ezt a projektet?')) {
      try {
        await deleteProject(projectId);
      } catch (error) {
        console.error('Error deleting project:', error);
        alert(t('projects.deleteError') || 'Hiba történt a projekt törlése során');
      }
    }
  };

  // Projekt szerkesztés
  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setShowForm(true);
  };

  // Projekt megtekintés
  const handleViewProject = (project: Project) => {
    setViewingProject(project);
  };

  // Státusz frissítés
  const handleUpdateStatus = async (projectId: string, status: Project['status']) => {
    try {
      await updateProjectStatus(projectId, status);
      toast.success(t('projects.messages.statusUpdated') || 'Státusz sikeresen frissítve!');
    } catch (error) {
      console.error('Error updating project status:', error);
      toast.error(t('projects.messages.statusUpdateError') || 'Hiba történt a státusz frissítése során');
    }
  };

  // Prioritás frissítés
  const handleUpdatePriority = async (projectId: string, priority: Project['priority']) => {
    try {
      await updateProjectPriority(projectId, priority);
      toast.success(t('projects.messages.priorityUpdated') || 'Prioritás sikeresen frissítve!');
    } catch (error) {
      console.error('Error updating project priority:', error);
      toast.error(t('projects.messages.priorityUpdateError') || 'Hiba történt a prioritás frissítése során');
    }
  };

  // Szűrők resetelése
  const resetFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  // Statisztikák
  const stats = getProjectStats();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <CommonPageHeader
        title={t('projects.title') || 'Projektek'}
        subtitle={t('projects.subtitle') || 'Projektjeinek kezelése és nyomon követése'}
        icon={ChartBarIcon}
      >
        <button
          onClick={() => {
            setEditingProject(null);
            setShowForm(true);
          }}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>{t('projects.new') || 'Új projekt'}</span>
        </button>
      </CommonPageHeader>

      <div className="w-full px-4 py-4 md:px-6 md:py-6">
        {/* Statisztikák */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    {t('projects.stats.total') || 'Összes projekt'}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {stats.total}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{stats.active}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    {t('projects.stats.active') || 'Aktív'}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {stats.active}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{stats.completed}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    {t('projects.stats.completed') || 'Befejezett'}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {stats.completed}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{stats.overdueCount}</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    {t('projects.stats.overdue') || 'Lejárt'}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {stats.overdueCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Keresés és szűrők */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder={t('projects.searchPlaceholder') || 'Keresés projektekben...'}
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                {t('common.filters') || 'Szűrők'}
              </button>
              <button
                onClick={resetFilters}
                className="flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                {t('common.reset') || 'Alaphelyzet'}
              </button>
            </div>
          </div>

          {/* Szűrők panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('projects.fields.status') || 'Státusz'}
                  </label>
                  <select
                    value={filters.status || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as Project['status'] || undefined }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">{t('common.all') || 'Minden'}</option>
                    <option value="planning">{t('projects.status.planning') || 'Tervezés'}</option>
                    <option value="active">{t('projects.status.active') || 'Aktív'}</option>
                    <option value="completed">{t('projects.status.completed') || 'Befejezett'}</option>
                    <option value="paused">{t('projects.status.paused') || 'Szünetel'}</option>
                    <option value="cancelled">{t('projects.status.cancelled') || 'Megszakítva'}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('projects.fields.priority') || 'Prioritás'}
                  </label>
                  <select
                    value={filters.priority || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value as Project['priority'] || undefined }))}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">{t('common.all') || 'Minden'}</option>
                    <option value="low">{t('projects.priority.low') || 'Alacsony'}</option>
                    <option value="medium">{t('projects.priority.medium') || 'Közepes'}</option>
                    <option value="high">{t('projects.priority.high') || 'Magas'}</option>
                    <option value="urgent">{t('projects.priority.urgent') || 'Sürgős'}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('projects.resultsCount') || 'Eredmények'}
                  </label>
                  <div className="text-sm text-gray-600 dark:text-gray-400 py-2">
                    {filteredProjects.length} / {projects.length} projekt
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Projektek listája */}
        <ProjectList
          projects={filteredProjects}
          loading={loading}
          onEdit={handleEditProject}
          onDelete={handleDeleteProject}
          onView={handleViewProject}
          onUpdateStatus={handleUpdateStatus}
          onUpdatePriority={handleUpdatePriority}
        />
      </div>

      {/* Projekt form modal */}
      <ProjectForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingProject(null);
        }}
        onSubmit={editingProject ? handleUpdateProject : handleCreateProject}
        initialData={editingProject || undefined}
      />

      {/* Projekt view modal */}
      {viewingProject && (
        <ProjectView
          project={viewingProject}
          isOpen={Boolean(viewingProject)}
          onClose={() => setViewingProject(null)}
        />
      )}
    </div>
  );
};

export default Projects; 